// Service para comunicação com a API da agenda
import { getApiUrl } from '../config/environment';

// Tipos TypeScript para a agenda
export interface Event {
  id: string;
  title: string;
  description?: string;
  start_time: string;
  end_time: string;
  event_date: string;
  location?: string;
  event_type: 'meeting' | 'call' | 'task' | 'reminder';
  color: string;
  is_all_day: boolean;
  is_recurring?: boolean;
  recurring_type?: 'daily' | 'weekly' | 'monthly';
  recurring_end_date?: string;
  calendar_id?: string;
  created_at: string;
  updated_at: string;
  calendar_name?: string;
  calendar_color?: string;
  created_by_name?: string;
  created_by_email?: string;
}

export interface Calendar {
  id: string;
  name: string;
  description?: string;
  color: string;
  is_default: boolean;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  event_count?: number;
}

export interface EventParticipant {
  id: string;
  user_id: string;
  name: string;
  email: string;
  color?: string;
  avatar_url?: string;
  status: 'pending' | 'accepted' | 'declined' | 'tentative';
  response_at?: string;
  created_at: string;
}

export interface EventStats {
  total_events: number;
  future_events: number;
  events_this_month: number;
  meetings: number;
  calls: number;
  tasks: number;
  reminders: number;
}

export interface Notification {
  id: string;
  notification_type: string;
  message: string;
  is_read: boolean;
  scheduled_at?: string;
  sent_at?: string;
  created_at: string;
  event_title?: string;
  event_date?: string;
  start_time?: string;
}

export interface AgendaSettings {
  default_view: 'day' | 'week' | 'month';
  week_starts_on: number; // 0 = Sunday, 1 = Monday
  working_hours_start: string;
  working_hours_end: string;
  timezone: string;
  email_notifications: boolean;
  popup_notifications: boolean;
}

// Cliente HTTP para comunicação com a API
class ApiClient {
  private timeout: number = 3000; // 3 segundos timeout padrão

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    customTimeout?: number
  ): Promise<T> {
    // Construir URL completa usando getApiUrl
    const url = getApiUrl(endpoint);
    
    if (!url) {
      throw new Error('API URL não configurada. Verifique REACT_APP_API_URL ou REACT_APP_BACKEND_URL');
    }
    
    // Obter token de autenticação
    const token = localStorage.getItem('token');
    
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    // Adicionar token se disponível
    if (token) {
      defaultHeaders['Authorization'] = `Bearer ${token}`;
    }

    const timeout = customTimeout || this.timeout;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const config: RequestInit = {
      ...options,
      signal: controller.signal,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorData: any = null;
        try {
          errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          // Se não conseguir parsear JSON, usar mensagem padrão
        }
        
        // Incluir status code na mensagem de erro para tratamento específico
        const error = new Error(errorMessage);
        (error as any).status = response.status;
        (error as any).statusText = response.statusText;
        (error as any).data = errorData;
        throw error;
      }

      return await response.json();
    } catch (error: any) {
      clearTimeout(timeoutId);
      
      // Se foi abortado por timeout
      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timeout');
        (timeoutError as any).status = 408;
        (timeoutError as any).statusText = 'Request Timeout';
        throw timeoutError;
      }
      
      console.error(`API request failed for ${endpoint}:`, {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
        url,
      });
      // Re-throw com mensagem mais clara, preservando status code
      const enhancedError = new Error(error.message || `Erro ao fazer requisição para ${endpoint}`);
      (enhancedError as any).status = error.status;
      (enhancedError as any).statusText = error.statusText;
      (enhancedError as any).data = error.data;
      throw enhancedError;
    }
  }

  async get<T>(endpoint: string, timeout?: number): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' }, timeout);
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Instância do cliente API
const apiClient = new ApiClient();

// Service da agenda
export const agendaService = {
  // =====================================================
  // EVENTOS
  // =====================================================

  async getEvents(filters?: {
    start_date?: string;
    end_date?: string;
    calendar_id?: string;
    event_type?: string;
  }): Promise<{ success: boolean; data: Event[]; count: number }> {
    try {
      // Usar endpoint webhook para listar eventos com timeout curto de 3 segundos
      const response = await apiClient.get<{ success: boolean; data: Event[]; count?: number }>('/webhooks/agenda/events', 3000);
      
      // Garantir que a resposta tenha o formato esperado
      if (response.success) {
        return {
          success: true,
          data: response.data || [],
          count: response.count || (response.data?.length || 0)
        };
      }
      
      // Se success for false, retornar array vazio
      return {
        success: false,
        data: [],
        count: 0
      };
    } catch (error: any) {
      // Tratamento específico para erro 503 (Service Unavailable)
      if (error.status === 503 || error.message?.includes('503') || error.message?.includes('Service Unavailable')) {
        console.warn('Serviço de eventos da agenda temporariamente indisponível (503). Retornando array vazio.');
        // Retornar array vazio em caso de 503 para não quebrar a aplicação
        return {
          success: false,
          data: [],
          count: 0
        };
      }
      
      console.warn('Erro ao buscar eventos da agenda:', {
        message: error.message,
        status: error.status,
        statusText: error.statusText,
      });
      // Retornar array vazio em caso de erro para não quebrar a aplicação
      return {
        success: false,
        data: [],
        count: 0
      };
    }
  },

  async createEvent(eventData: Omit<Event, 'id' | 'created_at' | 'updated_at'>): Promise<{ success: boolean; data: Event; message: string }> {
    // Usar endpoint webhook para criar eventos
    return apiClient.post('/webhooks/agenda/event', eventData);
  },

  // Métodos não implementados - retornar dados vazios para evitar erros
  async getEventById(id: string): Promise<{ success: boolean; data: Event }> {
    return { success: true, data: {} as Event };
  },

  async updateEvent(id: string, eventData: Partial<Event>): Promise<{ success: boolean; data: Event; message: string }> {
    return { success: true, data: {} as Event, message: 'Atualização não implementada' };
  },

  async deleteEvent(id: string): Promise<{ success: boolean; message: string }> {
    // Usar endpoint webhook para excluir eventos
    return apiClient.delete(`/webhooks/agenda/event/${id}`);
  },

  async searchEvents(query: string, filters?: {
    start_date?: string;
    end_date?: string;
    event_type?: string;
  }): Promise<{ success: boolean; data: Event[]; count: number }> {
    return { success: true, data: [], count: 0 };
  },

  async getUpcomingEvents(limit: number = 10): Promise<{ success: boolean; data: Event[]; count: number }> {
    return { success: true, data: [], count: 0 };
  },

  async getEventStats(): Promise<{ success: boolean; data: EventStats }> {
    return { success: true, data: {
      total_events: 0,
      future_events: 0,
      events_this_month: 0,
      meetings: 0,
      calls: 0,
      tasks: 0,
      reminders: 0
    }};
  },

  // =====================================================
  // CALENDÁRIOS (não implementados)
  // =====================================================

  async getCalendars(): Promise<{ success: boolean; data: Calendar[]; count: number }> {
    return { success: true, data: [], count: 0 };
  },

  async createCalendar(calendarData: Omit<Calendar, 'id' | 'created_at' | 'updated_at' | 'event_count'>): Promise<{ success: boolean; data: Calendar; message: string }> {
    return { success: true, data: {} as Calendar, message: 'Criação não implementada' };
  },

  async updateCalendar(id: string, calendarData: Partial<Calendar>): Promise<{ success: boolean; data: Calendar; message: string }> {
    return { success: true, data: {} as Calendar, message: 'Atualização não implementada' };
  },

  async deleteCalendar(id: string): Promise<{ success: boolean; message: string }> {
    return { success: true, message: 'Exclusão não implementada' };
  },

  // =====================================================
  // PARTICIPANTES (não implementados)
  // =====================================================

  async getEventParticipants(eventId: string): Promise<{ success: boolean; data: EventParticipant[]; count: number }> {
    return { success: true, data: [], count: 0 };
  },

  async addParticipant(eventId: string, userId: string, status: 'pending' | 'accepted' | 'declined' | 'tentative' = 'pending'): Promise<{ success: boolean; data: EventParticipant; message: string }> {
    return { success: true, data: {} as EventParticipant, message: 'Adição não implementada' };
  },

  async updateParticipantStatus(eventId: string, userId: string, status: 'pending' | 'accepted' | 'declined' | 'tentative'): Promise<{ success: boolean; data: EventParticipant; message: string }> {
    return { success: true, data: {} as EventParticipant, message: 'Atualização não implementada' };
  },

  async removeParticipant(eventId: string, userId: string): Promise<{ success: boolean; message: string }> {
    return { success: true, message: 'Remoção não implementada' };
  },

  // =====================================================
  // USUÁRIOS (não implementados)
  // =====================================================

  async getUsers(): Promise<{ success: boolean; data: any[]; count: number }> {
    return { success: true, data: [], count: 0 };
  },

  async createUser(userData: { name: string; email: string; avatar_url?: string; color?: string }): Promise<{ success: boolean; data: any; message: string }> {
    return { success: true, data: {}, message: 'Criação não implementada' };
  },

  // =====================================================
  // NOTIFICAÇÕES (não implementadas)
  // =====================================================

  async getNotifications(unreadOnly: boolean = false): Promise<{ success: boolean; data: Notification[]; count: number }> {
    return { success: true, data: [], count: 0 };
  },

  async markNotificationAsRead(id: string): Promise<{ success: boolean; data: Notification; message: string }> {
    return { success: true, data: {} as Notification, message: 'Marcação não implementada' };
  },

  async deleteNotification(id: string): Promise<{ success: boolean; message: string }> {
    return { success: true, message: 'Exclusão não implementada' };
  },

  // =====================================================
  // CONFIGURAÇÕES (não implementadas)
  // =====================================================

  async getSettings(): Promise<{ success: boolean; data: AgendaSettings | null }> {
    return { success: true, data: null };
  },

  async updateSettings(settings: Partial<AgendaSettings>): Promise<{ success: boolean; data: AgendaSettings; message: string }> {
    return { success: true, data: {} as AgendaSettings, message: 'Atualização não implementada' };
  }
};

export default agendaService;
